/**
 * Implement a Hero in Jumbotron form
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Container, Row, Col } from 'reactstrap'
import cx from 'classnames'
import CoverImage from 'client/shared/js/components/CoverImage'
import styles from './HeroJumbotron.module.scss'

const HeroJumbotron = ({
  className,
  photoUrl,
  backgroundFilter,
  container,
  fluid,
  children,
  rightChildren
}) => {
  const RightHeroContent = useMemo(() => (rightChildren) ? (
    <div className={cx(styles['hero-content'], 'hero-content')}>
      {rightChildren}
    </div>
  ) : null, [rightChildren])

  const HeroContent = useMemo(() => (
    <div className={cx(styles['hero-content'], 'hero-content', {
      [styles['left']]: !!RightHeroContent
    })}>
      {children}
    </div>
  ), [children, RightHeroContent])

  const WrappedHero = useMemo(() => (
    (container) ? (
      <Container fluid={fluid}>
        <Row className={styles['row']}>
          <Col
            className={cx('d-flex justify-content-center align-items-center')}
            lg={RightHeroContent ? 6 : 12}>
            {HeroContent}
          </Col>
          {(RightHeroContent) ? (
            <Col
              lg={HeroContent ? 6 : 12}>
              {RightHeroContent}
            </Col>
          ) : null}
        </Row>
      </Container>
    ) : HeroContent
  ), [container, fluid, HeroContent, RightHeroContent])

  return (
    <section className={cx('hero', className)}>
      <CoverImage
        photoUrl={photoUrl}
        backgroundFilter={backgroundFilter}>
        {WrappedHero}
      </CoverImage>
    </section>
  )
}

HeroJumbotron.defaultProps = {
  backgroundFilter: 'full'
}

HeroJumbotron.propTypes = {
  className: PropTypes.string,
  photoUrl: PropTypes.string,
  backgroundFilter: PropTypes.oneOf(['top', 'bottom', 'full', false]),
  container: PropTypes.bool,
  fluid: PropTypes.bool,
  children: PropTypes.node,
  rightChildren: PropTypes.node
}

export default HeroJumbotron
